import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import {
  Block,
  Confirm,
  Field,
  Input,
  NumInput,
  Pill,
  Segment,
  Select,
  Thumb,
  useToast,
} from '../ui';
import { pickFile } from '../media/capture';
import useInstallable from '../pwa/useInstallable';
import { isInstalled } from '../pwa/swRegister';
import { preloadStrings, tr } from '../i18n';
import useMedia from '../media/useMedia';
import { estimateUsage, requestPersistence } from '../data/db';
import { clearMeta, readMeta } from '../data/repo';
import { META } from '../data/schema';
import {
  CARD_Q,
  CURRENCIES,
  LANGUAGES,
  LEN_UNITS,
  OUTREACH_EN,
  OUTREACH_ZH,
  THEMES,
  WT_UNITS,
} from '../data/constants';
import ReportViewer from '../reports/ReportViewer';
import { suppliersCsv, finalReviewCsv, downloadCsv, csvStamp } from '../reports/csv';
import { p } from '../routes';

/**
 * Settings (spec § 5.16).
 *
 * Also the app's maintenance console: backup/restore, exports, storage health
 * and the destructive actions. The backup matters more here than in a normal
 * app — on a trip it is the only copy of a day's work that is not on one phone.
 */
export default function SettingsScreen() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const {
    fairs,
    fair,
    suppliers,
    visibleSuppliers,
    settings,
    saveSettings,
    deleteAllData,
    counts,
    saveFair,
    saveSupplier,
    syncNow,
    sync,
    reload,
  } = useFairTracker();

  const open = new URLSearchParams(location.search).get('open') || '';
  const [draft, setDraft] = useState(settings || {});
  const [stats, setStats] = useState(null);
  const [storage, setStorage] = useState(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [report, setReport] = useState(null);
  const media = useMedia({});
  const { canInstall, install, installed } = useInstallable();
  const installedApp = installed || isInstalled();

  useEffect(() => setDraft(settings || {}), [settings]);

  useEffect(() => {
    counts().then(setStats);
    estimateUsage().then(setStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    try {
      await saveSettings(next);
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const setProfile = (k) => (v) => save({ profile: { ...(draft.profile || {}), [k]: v } });

  /* ── Backup (spec § 8.4) ───────────────────────────────────────────────
     version 2, matching the prototype, so a backup taken from the prototype
     restores here and vice versa. Media is referenced, not embedded — the
     bytes live on the server and in IndexedDB. */
  const exportBackup = async () => {
    try {
      const payload = {
        version: 2,
        exportedAt: new Date().toISOString(),
        settings: draft,
        fairs,
        suppliers,
      };
      download(
        `fair-tracker-backup-${new Date().toISOString().slice(0, 10)}.json`,
        JSON.stringify(payload, null, 2),
        'application/json',
      );
      toast('Backup exported');
    } catch (e) {
      toast('Could not export', 'error');
    }
  };

  const restoreBackup = async () => {
    const [file] = await pickFile({ accept: 'application/json,.json' });
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || !Array.isArray(data.suppliers)) {
        toast('That file is not a Fair Tracker backup');
        return;
      }
      // Merge by id — saveFair/saveSupplier upsert, so restoring the same
      // backup twice is a no-op rather than a duplicate.
      for (const f of data.fairs || []) await saveFair(f);
      for (const s of data.suppliers) await saveSupplier(s);
      if (data.settings) await saveSettings({ ...draft, ...data.settings });
      await reload();
      toast(`Restored ${data.suppliers.length} suppliers`);
    } catch (e) {
      toast('Could not read that backup', 'error');
    }
  };

  const wipe = async () => {
    setConfirmWipe(false);
    try {
      await deleteAllData();
      toast('All Fair Tracker data deleted');
      history.push(p('/fairs'));
    } catch (e) {
      toast('Could not delete', 'error');
    }
  };

  const previewFirstRun = async () => {
    await clearMeta(META.WELCOMED);
    await reload();
    history.push(p('/welcome'));
  };

  const askPersistence = async () => {
    const granted = await requestPersistence();
    toast(
      granted
        ? 'Storage is now persistent — it will not be evicted'
        : 'The browser declined. Install to your home screen and try again.',
    );
    setStorage(await estimateUsage());
  };

  const questions = draft.cardQuestions || [];

  const setQuestion = (id, patch) =>
    save({ cardQuestions: questions.map((q) => (q.id === id ? { ...q, ...patch } : q)) });

  const moveQuestion = (index, delta) => {
    const next = [...questions];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    save({ cardQuestions: next });
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Settings")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {tr("Defaults, your card, exports and data")}
        </p>
      </div>

      {/* ── Sync status ─────────────────────────────────────────────────── */}
      <section className="ft-card">
        <div className="ft-planhead">
          <div>
            <h2 className="ft-h2">{tr("Sync")}</h2>
            <p className="ft-sub" style={{ margin: 0 }}>
              {sync.status === 'offline'
                ? 'Offline — everything is saved on this device and will sync when you reconnect.'
                : sync.status === 'syncing'
                ? 'Syncing…'
                : sync.status === 'error'
                ? `Last attempt failed: ${sync.error}`
                : sync.lastSyncAt
                ? `Last synced ${new Date(sync.lastSyncAt).toLocaleString()}`
                : 'Not synced yet'}
            </p>
          </div>
          {sync.pending > 0 && <Pill tone="maybe">{sync.pending} waiting</Pill>}
        </div>
        <button type="button" className="ft-btn ft-btn-block" onClick={() => syncNow({ full: false })}>
          <Icon name="RefreshCw" size={16} /> {tr("Sync now")}
        </button>
      </section>

      {/* ── Quick Entry defaults ────────────────────────────────────────── */}
      <Block title={tr("Defaults for Quick Entry")} defaultOpen>
        <div className="ft-grid-3">
          <Field label={tr("Currency")}>
            <Select value={draft.currency} options={CURRENCIES} onChange={(v) => save({ currency: v })} />
          </Field>
          <Field label={tr("Length unit")}>
            <Select value={draft.len} options={LEN_UNITS} onChange={(v) => save({ len: v })} />
          </Field>
          <Field label={tr("Weight unit")}>
            <Select value={draft.wt} options={WT_UNITS} onChange={(v) => save({ wt: v })} />
          </Field>
        </div>
        <Field
          label={tr("Quick landed-cost factor")}
          hint={tr("Multiplies the quoted price to estimate landed cost before you leave the booth (rule M1). 1.35 by default.")}
        >
          <NumInput value={draft.landedFactor} onChange={(v) => save({ landedFactor: v })} />
        </Field>
      </Block>

      {/* ── Appearance & language ───────────────────────────────────────── */}
      <Block title={tr("Appearance & language")} onOpen={preloadStrings}>
        <Field label={tr("Theme")}>
          <Segment
            value={draft.theme || 'auto'}
            options={THEMES.map((t) => ({ value: t, label: t[0].toUpperCase() + t.slice(1) }))}
            onChange={(v) => save({ theme: v })}
          />
        </Field>
        <Field label={tr("Language")}>
          <Segment
            value={draft.language || 'en'}
            options={LANGUAGES.map(([code, name, flag]) => ({ value: code, label: `${flag} ${name}` }))}
            onChange={(v) => save({ language: v })}
          />
        </Field>
        <p className="ft-hint">
          The supplier card stays bilingual (English + 中文) whatever you pick here — that is for the
          supplier to read, not you.
        </p>
      </Block>

      {/* ── My company card ─────────────────────────────────────────────── */}
      <Block title={tr("My company card")} defaultOpen={open === 'card'}>
        <Field label={tr("Company")}>
          <Input value={(draft.profile || {}).company || ''} onChange={setProfile('company')} />
        </Field>
        <Field label={tr("Your name")}>
          <Input value={(draft.profile || {}).name || ''} onChange={setProfile('name')} />
        </Field>
        <div className="ft-grid-2">
          <Field label={tr("Title")}>
            <Input value={(draft.profile || {}).title || ''} onChange={setProfile('title')} />
          </Field>
          <Field label={tr("Phone")}>
            <Input value={(draft.profile || {}).phone || ''} onChange={setProfile('phone')} type="tel" />
          </Field>
          <Field label="WhatsApp">
            <Input value={(draft.profile || {}).whatsapp || ''} onChange={setProfile('whatsapp')} type="tel" />
          </Field>
          <Field label={tr("WeChat ID")}>
            <Input value={(draft.profile || {}).wechat || ''} onChange={setProfile('wechat')} />
          </Field>
          <Field label={tr("Email")}>
            <Input value={(draft.profile || {}).email || ''} onChange={setProfile('email')} type="email" />
          </Field>
          <Field label={tr("Website")}>
            <Input value={(draft.profile || {}).website || ''} onChange={setProfile('website')} />
          </Field>
          <Field label={tr("Port")}>
            <Input value={(draft.profile || {}).port || ''} onChange={setProfile('port')} />
          </Field>
        </div>
        <Field label={tr("Address")}>
          <Input value={(draft.profile || {}).address || ''} onChange={setProfile('address')} />
        </Field>
        <Field label={tr("Intro (English)")}>
          <Input value={(draft.profile || {}).intro || ''} onChange={setProfile('intro')} />
        </Field>
        <Field label="Intro (中文)">
          <Input value={(draft.profile || {}).introZh || ''} onChange={setProfile('introZh')} />
        </Field>

        <h3 className="ft-h2" style={{ marginTop: 14 }}>
          {tr("WeChat QR image")}
        </h3>
        <p className="ft-sub">
          WeChat does not let anyone generate this — take a screenshot of your own QR in WeChat and
          upload it here.
        </p>
        {(draft.profile || {}).wechatQr ? (
          <div className="ft-row">
            <Thumb photo={{ path: draft.profile.wechatQr }} size={96} rounded={false} alt="WeChat QR" />
            <button type="button" className="ft-btn ft-btn-quiet" onClick={() => setProfile('wechatQr')('')}>
              {tr("Remove")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="ft-btn"
            onClick={async () => {
              const refs = await media.addPhotos({ existing: [], tag: 'WeChat QR' });
              const ref = refs[refs.length - 1];
              if (ref) setProfile('wechatQr')(ref.mediaId);
            }}
          >
            <Icon name="Upload" size={16} /> {tr("Upload WeChat QR")}
          </button>
        )}

        <Link className="ft-btn ft-btn-block" style={{ marginTop: 10 }} to={p('/card')}>
          {tr("Preview card")}
        </Link>
      </Block>

      {/* ── Questions for suppliers ─────────────────────────────────────── */}
      <Block title={tr("Questions for suppliers")} defaultOpen={open === 'questions'}>
        <p className="ft-sub">
          Shown on your card, numbered, Chinese large. These are what you point at when there is no
          common language.
        </p>
        {questions.map((q, i) => (
          <div className="ft-qedit" key={q.id}>
            <span className="ft-qedit-n">{i + 1}</span>
            <div className="ft-qedit-fields">
              <Input value={q.en} onChange={(v) => setQuestion(q.id, { en: v })} placeholder="English" />
              <Input value={q.zh} onChange={(v) => setQuestion(q.id, { zh: v })} placeholder="中文" />
            </div>
            <div className="ft-qedit-actions">
              <button
                type="button"
                className="ft-icon-btn"
                onClick={() => moveQuestion(i, -1)}
                aria-label="Move up"
                disabled={i === 0}
              >
                <Icon name="ChevronUp" size={16} />
              </button>
              <button
                type="button"
                className="ft-icon-btn"
                onClick={() => moveQuestion(i, 1)}
                aria-label="Move down"
                disabled={i === questions.length - 1}
              >
                <Icon name="ChevronDown" size={16} />
              </button>
              <button
                type="button"
                className="ft-icon-btn"
                onClick={() => save({ cardQuestions: questions.filter((x) => x.id !== q.id) })}
                aria-label="Remove question"
              >
                <Icon name="Trash2" size={15} />
              </button>
            </div>
          </div>
        ))}
        <div className="ft-row" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="ft-btn"
            onClick={() =>
              save({ cardQuestions: [...questions, { id: `q${Date.now()}`, en: '', zh: '' }] })
            }
          >
            <Icon name="Plus" size={16} /> {tr("Add question")}
          </button>
          <button
            type="button"
            className="ft-btn"
            onClick={() =>
              save({ cardQuestions: CARD_Q.map(([en, zh], i) => ({ id: `q${i + 1}`, en, zh })) })
            }
          >
            {tr("Reset to defaults")}
          </button>
        </div>
      </Block>

      {/* ── Outreach templates ──────────────────────────────────────────── */}
      <Block title={tr("Message templates")}>
        <Field label="English">
          <Input
            value={(draft.outreach || {}).en || OUTREACH_EN}
            onChange={(v) => save({ outreach: { ...(draft.outreach || {}), en: v } })}
          />
        </Field>
        <Field label="中文">
          <Input
            value={(draft.outreach || {}).zh || OUTREACH_ZH}
            onChange={(v) => save({ outreach: { ...(draft.outreach || {}), zh: v } })}
          />
        </Field>
        <Link className="ft-btn ft-btn-block" to={p('/outreach')}>
          {tr("Open outreach")}
        </Link>
      </Block>

      {/* ── Reports & exports ───────────────────────────────────────────── */}
      <Block title={tr("Reports & exports")} defaultOpen={open === 'reports' || open === 'backup'}>
        <h3 className="ft-h2">{tr("Reports")}</h3>
        <p className="ft-sub">
          {tr("Opens in the viewer, where Print → Save as PDF keeps Chinese text intact.")}
        </p>
        <div className="ft-btnrow">
          <button type="button" className="ft-btn" onClick={() => setReport('fair')}>
            <Icon name="Printer" size={16} /> {tr("Fair report")}
          </button>
          <button type="button" className="ft-btn" onClick={() => setReport('shortlist')}>
            <Icon name="Printer" size={16} /> {tr("Final shortlist")}
          </button>
        </div>

        <h3 className="ft-h2" style={{ marginTop: 14 }}>
          {tr("Spreadsheets")}
        </h3>
        <div className="ft-btnrow">
          <button
            type="button"
            className="ft-btn"
            onClick={() => {
              downloadCsv(
                `suppliers-${csvStamp()}.csv`,
                suppliersCsv({ suppliers: visibleSuppliers, fairs }),
              );
              toast('Suppliers CSV exported');
            }}
          >
            <Icon name="Download" size={16} /> {tr("Suppliers .csv")}
          </button>
          <button
            type="button"
            className="ft-btn"
            onClick={() => {
              downloadCsv(
                `final-review-${csvStamp()}.csv`,
                finalReviewCsv({ suppliers: visibleSuppliers, fairs }),
              );
              toast('Final review CSV exported');
            }}
          >
            <Icon name="Download" size={16} /> {tr("Final review .csv")}
          </button>
        </div>

        <h3 className="ft-h2" style={{ marginTop: 14 }}>
          {tr("Backup")}
        </h3>
        <div className="ft-btnrow">
          <button type="button" className="ft-btn ft-btn-primary" onClick={exportBackup}>
            <Icon name="Download" size={16} /> {tr("Export backup (.json)")}
          </button>
          <button type="button" className="ft-btn" onClick={restoreBackup}>
            <Icon name="Upload" size={16} /> {tr("Restore backup")}
          </button>
        </div>
        <p className="ft-hint">
          Export every evening on a trip. It is the only copy of the day that is not on this one
          phone.
        </p>
      </Block>

      {/* ── Fairs ───────────────────────────────────────────────────────── */}
      <Block title={tr("Fairs")}>
        <div className="ft-btnrow">
          <Link className="ft-btn" to={p('/fairs')}>
            {tr("Manage fairs")}
          </Link>
          <button type="button" className="ft-btn" onClick={previewFirstRun}>
            {tr("Preview first-run screen")}
          </button>
        </div>
      </Block>

      {/* ── Data & storage ──────────────────────────────────────────────── */}
      <Block title={tr("Data & storage")}>
        {stats && (
          <dl className="ft-glance">
            <div>
              <dt>{tr("Fairs")}</dt>
              <dd>{stats.fairs}</dd>
            </div>
            <div>
              <dt>{tr("Suppliers")}</dt>
              <dd>{stats.suppliers}</dd>
            </div>
            <div>
              <dt>{tr("Products")}</dt>
              <dd>{stats.products}</dd>
            </div>
            <div>
              <dt>{tr("Media files")}</dt>
              <dd>{stats.media}</dd>
            </div>
            <div>
              <dt>{tr("Media size")}</dt>
              <dd>{mb(stats.mediaBytes)}</dd>
            </div>
            <div>
              <dt>{tr("Waiting to sync")}</dt>
              <dd>{stats.queued}</dd>
            </div>
          </dl>
        )}

        {storage && (
          <>
            <p className="ft-sub" style={{ marginTop: 12 }}>
              Using {mb(storage.usage)} of {mb(storage.quota)} available (
              {Math.round(storage.ratio * 100)}%).
            </p>
            {storage.ratio > 0.8 && (
              <p className="ft-warn">
                <Icon name="AlertTriangle" size={14} /> Storage is nearly full. Export a backup and
                sync before capturing more.
              </p>
            )}
          </>
        )}

        <button type="button" className="ft-btn ft-btn-block" style={{ marginTop: 10 }} onClick={askPersistence}>
          {tr("Keep my data permanently")}
        </button>
        <p className="ft-hint">
          Asks the browser not to evict Fair Tracker. iOS can clear site data after a week unused,
          which on a five-day trip would be the whole capture.
        </p>

        <h3 className="ft-h2" style={{ marginTop: 16 }}>
          {tr("Use it offline")}
        </h3>
        {installedApp ? (
          <p className="ft-sub">
            <Icon name="CheckCircle" size={14} /> Installed. The app opens from your home screen and
            works with no signal.
          </p>
        ) : (
          <>
            <p className="ft-sub">
              Install Fair Tracker to your home screen before you fly. That is what lets it open in
              a hall with no connection at all.
            </p>
            {canInstall ? (
              <button type="button" className="ft-btn ft-btn-green ft-btn-block" onClick={install}>
                <Icon name="Download" size={16} /> {tr("Install Fair Tracker")}
              </button>
            ) : (
              <p className="ft-hint">
                Your browser does not offer a one-tap install here. On iPhone use Safari&apos;s Share
                button → <b>{tr("Add to Home Screen")}</b>; on Android Chrome use the menu →{' '}
                <b>{tr("Install app")}</b>.
              </p>
            )}
          </>
        )}

        <button
          type="button"
          className="ft-btn ft-btn-danger ft-btn-block"
          style={{ marginTop: 14 }}
          onClick={() => setConfirmWipe(true)}
        >
          {tr("Delete all Fair Tracker data")}
        </button>
      </Block>

      {report && (
        <ReportViewer
          kind={report}
          suppliers={visibleSuppliers}
          fair={fair}
          onClose={() => setReport(null)}
        />
      )}

      {confirmWipe && (
        <Confirm
          title={tr("Delete everything?")}
          message={`All ${(stats && stats.suppliers) || 0} suppliers, ${
            (stats && stats.fairs) || 0
          } fairs and every photo on this device. Export a backup first if you are not certain.`}
          confirmLabel={tr("Delete everything")}
          onConfirm={wipe}
          onCancel={() => setConfirmWipe(false)}
        />
      )}
    </div>
  );
}

const mb = (bytes) => `${((bytes || 0) / 1048576).toFixed(1)} MB`;

function download(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export { readMeta };
